/**
 * Shared helpers for inventory products.
 * Supports both "unit" (whole units) and "use" (strokes/pumps etc.) tracking.
 */

export function isUseMode(p) {
  return p && p.trackingMode === 'use';
}

/** Available quantity: for unit = stock; for use = totalUsesAvailable */
export function getAvailable(p) {
  if (!p) return 0;
  if (isUseMode(p)) return Math.max(0, Number(p.totalUsesAvailable) || 0);
  return Math.max(0, Number(p.stock) || 0);
}

/** Min threshold: for unit = minStock; for use = usesMinThreshold */
export function getMinThreshold(p) {
  if (!p) return 0;
  if (isUseMode(p)) return Math.max(0, Number(p.usesMinThreshold) || 0);
  return Math.max(0, Number(p.minStock) || 0);
}

/** Derived unit count (e.g. bottles) for use-mode: totalUsesAvailable / usesPerUnit */
export function getDerivedUnitCount(p) {
  if (!p || !isUseMode(p)) return Number(p?.stock) ?? 0;
  const uses = Number(p.totalUsesAvailable) || 0;
  const per = Math.max(1, Number(p.usesPerUnit) || 1);
  return uses / per;
}

/** Cost per single use (for use-mode). costPrice is per unit (e.g. per bottle). */
export function getCostPerUse(p) {
  if (!p || !isUseMode(p)) return 0;
  const cost = Number(p.costPrice) || 0;
  const per = Math.max(1, Number(p.usesPerUnit) || 1);
  return cost / per;
}

/** For use-mode products, compute costPerUse and optionally store on product. */
export function ensureCostPerUse(p) {
  if (!p || !isUseMode(p)) return p;
  return { ...p, costPerUse: getCostPerUse(p) };
}

/** Check if product is low stock (above 0 but at or below threshold) */
export function isLowStock(p) {
  const avail = getAvailable(p);
  const min = getMinThreshold(p);
  return avail > 0 && avail <= min;
}

/** Check if product is out of stock */
export function isOutOfStock(p) {
  return getAvailable(p) === 0;
}
